import { useEffect, useRef, useState } from 'react'
import { countUpValue } from 'src/utils/countUp'

const DEFAULT_DURATION_MS = 700

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Animate a number toward `value`, easing out.
 *
 * Counts from wherever the display currently sits, so a stat that updates
 * mid-animation (a round settles while the card is open) slides on from the
 * value on screen instead of snapping back to zero and re-running.
 *
 * Honours prefers-reduced-motion by returning the value directly.
 */
export const useCountUp = (value: number, durationMs = DEFAULT_DURATION_MS): number => {
  // Starts at 0 so the FIRST paint animates too. The stats card mounts fresh
  // when the loading skeleton is replaced, and seeding state with the final
  // value made that reveal — the one moment a count-up is actually worth
  // watching — snap instead.
  const [displayed, setDisplayed] = useState(0)
  // Read via ref so a change of `value` mid-flight does not restart from 0.
  const displayedRef = useRef(value)
  displayedRef.current = displayed

  useEffect(() => {
    if (prefersReducedMotion() || displayedRef.current === value) {
      setDisplayed(value)
      return undefined
    }

    const from = displayedRef.current
    const startedAt = performance.now()
    let frame = 0

    const step = (now: number) => {
      const t = Math.min((now - startedAt) / durationMs, 1)
      setDisplayed(countUpValue(from, value, t))
      if (t < 1) frame = requestAnimationFrame(step)
    }

    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
    // displayedRef is intentionally not a dependency — it is the animation's
    // starting point, not a trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, durationMs])

  return displayed
}
